export type PaymentPermissions = {
  viewQueue: boolean;
  createManual: boolean;
  submitClient: boolean;
  uploadFile: boolean;
  reviewUpload: boolean;
  edit: boolean;
  match: boolean;
  allocate: boolean;
  overrideAllocation: boolean;
  verify: boolean;
  approve: boolean;
  reject: boolean;
  reverse: boolean;
  reviewDuplicate: boolean;
  overrideDuplicate: boolean;
  manageExceptions: boolean;
  viewDocuments: boolean;
  uploadDocuments: boolean;
  viewHistory: boolean;
  viewAudit: boolean;
  exportReport: boolean;
};

export type PaymentApprovalPolicy = {
  policyId: string;
  policyName: string;
  activeStatus: string;
  isDefault: boolean;
  verificationRequired: boolean;
  approvalRequired: boolean;
  twoPersonControlRequired: boolean;
  creatorMayVerify: boolean;
  verifierMayApprove: boolean;
  minimumApprovalAmountCents: number;
  managerApprovalThresholdCents: number | null;
  executiveApprovalThresholdCents: number | null;
  duplicateOverrideRequiresApproval: boolean;
  allocationOverrideRequiresApproval: boolean;
  overpaymentOverrideRequiresApproval: boolean;
  currencyMismatchRequiresApproval: boolean;
  reversalRequiresApproval: boolean;
  postApprovalCorrectionRequiresApproval: boolean;
  version: number;
  updatedAt: string;
};

export type PaymentApprovalResolution = {
  policy: PaymentApprovalPolicy;
  paymentAmountCents: number;
  requiredApprovalLevel: string;
  currentApprovalStage: string;
  eligibleApproverRoles: string[];
  completedApprovals: Array<{ approvalId: string; approvalLevel: string; approvalStage: string; decision: string; actorName: string; occurredAt: string }>;
  outstandingApprovals: string[];
  overrideTriggers: string[];
  blockedReason: string | null;
};

export type PaymentPromiseLink = {
  linkId: string;
  paymentId: string;
  promiseId: string;
  promiseReference: string | null;
  promisedAmountCents: number;
  promiseDueDate: string | null;
  confidence: string;
  matchingFactors: string[];
  recommendedOutcome: string;
  confirmedOutcome: string | null;
  status: string;
  updatedAt: string;
};

export type PaymentCorrection = {
  correctionId: string;
  originalPaymentId: string;
  replacementPaymentId: string | null;
  correctionType: string;
  requestedChanges: Record<string, unknown>;
  reason: string;
  requestedByUserId: string;
  requestedByName: string;
  requestedAt: string;
  approvalStatus: string;
  approvedByName: string | null;
  approvedAt: string | null;
  rejectedByName: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
};

export type PaymentClientAllocationInstruction = {
  instructionId: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  dueDate: string | null;
  originalAmountCents: number;
  operationalBalanceCents: number;
  currency: string;
  reportedAllocationCents: number;
  internalVerifiedAllocationCents: number | null;
  clientNote: string | null;
  reviewStatus: string;
  submittedAt: string;
};

export type PaymentClarification = {
  clarificationId: string;
  clarificationType: string;
  requestText: string;
  requestedByName: string;
  requestedAt: string;
  dueDate: string | null;
  status: string;
  clientResponse: string | null;
  respondedAt: string | null;
  resolution: string | null;
  resolvedAt: string | null;
};

export type CanonicalPayment = {
  paymentId: string;
  companyId: string;
  clientId: string;
  customerId?: string | null;
  customerName: string;
  customerNumber: string;
  accountId?: string | null;
  accountNumber: string;
  placementId?: string | null;
  paymentSource: string;
  externalPaymentReference?: string | null;
  clientPaymentReference?: string | null;
  paymentDate: string;
  reportedDate: string;
  receivedByClientDate?: string | null;
  amountCents: number;
  currency: string;
  paymentMethod: string;
  payerName?: string | null;
  payerAccountReference?: string | null;
  checkNumber?: string | null;
  achReference?: string | null;
  cardReferenceSuffix?: string | null;
  wireReference?: string | null;
  depositReference?: string | null;
  memo?: string | null;
  reportedByName: string;
  reportedByOrganization: string;
  verificationStatus: string;
  allocationStatus: string;
  operationalStatus: string;
  duplicateStatus: string;
  exceptionStatus: string;
  allocatedAmountCents: number;
  unappliedAmountCents: number;
  verifiedAt?: string | null;
  verifiedByName?: string | null;
  approvedAt?: string | null;
  approvedByName?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  reversedAt?: string | null;
  reversalReason?: string | null;
  correlationId: string;
  sourceUploadId?: string | null;
  sourceRowId?: string | null;
  replacementPaymentId?: string | null;
  correctionOfPaymentId?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type PaymentMetrics = {
  totalCount: number;
  totalAmountCents: number;
  pendingReview: number;
  unmatched: number;
  partiallyMatched: number;
  fullyAllocated: number;
  pendingVerification: number;
  verified: number;
  possibleDuplicates: number;
  rejected: number;
  reversed: number;
  exceptions: number;
};

export type PaymentInput = {
  clientId: string;
  customerId?: string | null;
  customerName: string;
  customerNumber?: string;
  accountId?: string | null;
  accountNumber?: string;
  placementId?: string | null;
  paymentSource: string;
  externalPaymentReference?: string;
  clientPaymentReference?: string;
  paymentDate: string;
  receivedByClientDate?: string | null;
  amountCents: number;
  currency: string;
  paymentMethod: string;
  payerName?: string;
  payerAccountReference?: string;
  checkNumber?: string;
  achReference?: string;
  cardReferenceSuffix?: string;
  wireReference?: string;
  depositReference?: string;
  memo?: string;
  reportedByOrganization: string;
  idempotencyKey: string;
};

export type PaymentAllocation = {
  allocationId: string;
  paymentId: string;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  accountId: string;
  placementId?: string | null;
  allocatedAmountCents: number;
  currency: string;
  allocationType: string;
  allocationStatus: string;
  createdByName: string;
  createdAt: string;
  approvedByName?: string | null;
  approvedAt?: string | null;
  reversedByName?: string | null;
  reversedAt?: string | null;
  reversalReason?: string | null;
  correlationId: string;
};

export type PaymentHistoryEntry = {
  actionId: string;
  actionType: string;
  previousStatus?: string | null;
  newStatus?: string | null;
  actorName: string;
  reason?: string | null;
  correlationId: string;
  occurredAt: string;
};

export type PaymentException = {
  exceptionId: string;
  paymentId?: string | null;
  uploadId?: string | null;
  uploadRowId?: string | null;
  category: string;
  status: string;
  ownerName?: string | null;
  reason: string;
  notes?: string | null;
  requestedInformation?: string | null;
  dueDate?: string | null;
  resolution?: string | null;
  resolvedByName?: string | null;
  resolvedAt?: string | null;
  correlationId: string;
  createdAt: string;
  updatedAt: string;
};

export type PaymentMatchSuggestion = {
  suggestionId: string;
  entityType: string;
  entityId: string;
  entityLabel: string;
  confidence: "EXACT" | "HIGH" | "MEDIUM" | "LOW" | "NONE";
  factors: string[];
  suggestedAmountCents?: number | null;
  status: string;
};

export type PaymentUpload = {
  uploadId: string;
  clientId: string;
  filename: string;
  fileType: string;
  fileSize: number;
  rowCount: number;
  uploadedByName: string;
  uploadedAt: string;
  processingStatus: string;
  validRowCount: number;
  invalidRowCount: number;
  duplicateRowCount: number;
  matchedRowCount: number;
  exceptionRowCount: number;
  completedAt?: string | null;
  correlationId: string;
  createdAt: string;
  updatedAt: string;
};

export type PaymentUploadRow = {
  rowId: string;
  uploadId: string;
  rowNumber: number;
  original: Record<string, unknown>;
  normalized: Record<string, unknown>;
  validationStatus: string;
  validationErrors: string[];
  duplicateStatus: string;
  exceptionReason?: string | null;
  proposedAction?: string | null;
  userDecision?: string | null;
  paymentId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PaymentDetail = {
  payment: CanonicalPayment;
  allocations: PaymentAllocation[];
  history: PaymentHistoryEntry[];
  audit: Array<{
    auditId: string;
    eventType: string;
    outcome: string;
    actorName: string;
    actorRole: string;
    correlationId: string;
    metadata: Record<string, unknown>;
    failureCategory?: string | null;
    safeMessage?: string | null;
    occurredAt: string;
  }>;
  exceptions: PaymentException[];
  matchingSuggestions: PaymentMatchSuggestion[];
  documents: Array<{
    documentId: string;
    paymentId?: string | null;
    documentType: string;
    fileName: string;
    contentType: string;
    fileSize: number;
    contentHash: string;
    uploadedByName: string;
    uploadedAt: string;
  }>;
  relatedStatements: Array<{
    statementId: string;
    statementNumber: string;
    statementDate?: string | null;
    balanceCents: number;
    lifecycleStatus: string;
  }>;
  permissions: PaymentPermissions;
};

export type PaymentQueueFilters = {
  companyId?: string;
  search?: string;
  clientId?: string;
  customerId?: string;
  accountId?: string;
  paymentSource?: string;
  paymentMethod?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  operationalStatus?: string;
  allocationStatus?: string;
  verificationStatus?: string;
  duplicateStatus?: string;
  exceptionStatus?: string;
  currency?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
};

export async function listPayments(filters: PaymentQueueFilters = {}) {
  return paymentRequest<{
    payments: CanonicalPayment[];
    metrics: PaymentMetrics;
    pagination: { page: number; pageSize: number; total: number; pageCount: number };
    permissions: PaymentPermissions;
    operationalPermissions: { manageApprovalPolicy: boolean };
    actorUserId: string;
  }>(`/api/ar-recovery/payments?${params(filters)}`);
}

export async function getPayment(paymentId: string, companyId?: string) {
  return paymentRequest<PaymentDetail>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}?${params({ companyId })}`);
}

export async function createPayment(payment: PaymentInput, companyId?: string) {
  return paymentRequest<{ payment: CanonicalPayment; created: boolean; replay: boolean }>("/api/ar-recovery/payments", {
    method: "POST",
    body: JSON.stringify({ companyId, payment }),
  });
}

export async function updatePayment(paymentId: string, patch: Partial<PaymentInput> & { reason: string; idempotencyKey: string }, companyId?: string) {
  return paymentRequest<{ payment: CanonicalPayment }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}`, {
    method: "PATCH",
    body: JSON.stringify({ companyId, ...patch }),
  });
}

export async function transitionPayment(
  paymentId: string,
  action: "start_verification" | "verify" | "approve" | "reject" | "reverse" | "confirm_duplicate" | "mark_distinct",
  reason: string,
  companyId?: string,
) {
  if (action === "approve") {
    return paymentRequest<{ payment: CanonicalPayment }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/approval`, {
      method: "POST",
      body: JSON.stringify({ companyId, decision: "APPROVE", reason, idempotencyKey: `payment-policy-approval:${paymentId}:${crypto.randomUUID()}` }),
    }).then(async () => ({ payment: (await getPayment(paymentId, companyId)).payment }));
  }
  return paymentRequest<{ payment: CanonicalPayment }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/actions`, {
    method: "POST",
    body: JSON.stringify({ companyId, action, reason, idempotencyKey: `payment:${paymentId}:${action}:${crypto.randomUUID()}` }),
  });
}

export async function listPaymentApprovalPolicies(companyId?: string) {
  return paymentRequest<{ policies: PaymentApprovalPolicy[] }>(`/api/ar-recovery/payments/approval-policies?${params({ companyId })}`);
}

export async function savePaymentApprovalPolicy(policy: Omit<PaymentApprovalPolicy, "policyId" | "version" | "updatedAt"> & { policyId?: string }, companyId?: string) {
  return paymentRequest<{ policy: PaymentApprovalPolicy }>("/api/ar-recovery/payments/approval-policies", {
    method: "POST",
    body: JSON.stringify({ companyId, policy: { ...policy, idempotencyKey: `payment-policy:${crypto.randomUUID()}` } }),
  });
}

export async function getPaymentApprovalResolution(paymentId: string, companyId?: string) {
  return paymentRequest<PaymentApprovalResolution>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/approval?${params({ companyId })}`);
}

export async function requestPaymentCorrection(
  paymentId: string,
  input: { correctionType: string; requestedChanges: Record<string, unknown>; reason: string },
  companyId?: string,
) {
  return paymentRequest<{ correction: PaymentCorrection }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/corrections`, {
    method: "POST",
    body: JSON.stringify({ companyId, ...input, idempotencyKey: `payment-correction:${paymentId}:${crypto.randomUUID()}` }),
  });
}

export async function listPaymentCorrections(paymentId: string, companyId?: string) {
  return paymentRequest<{ corrections: PaymentCorrection[] }>(
    `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/corrections?${params({ companyId })}`,
  );
}

export async function decidePaymentCorrection(
  correctionId: string,
  input: { decision: "APPROVE" | "REJECT"; reason?: string },
  companyId?: string,
) {
  return paymentRequest<{ correction: PaymentCorrection }>(
    `/api/ar-recovery/payments/corrections/${encodeURIComponent(correctionId)}`,
    {
      method: "POST",
      body: JSON.stringify({ companyId, ...input, idempotencyKey: `payment-correction-decision:${correctionId}:${input.decision}` }),
    },
  );
}

export async function listClientAllocationInstructions(paymentId: string, companyId?: string) {
  return paymentRequest<{ instructions: PaymentClientAllocationInstruction[] }>(
    `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/client-allocations?${params({ companyId })}`,
  );
}

export function getPaymentDocumentUrl(paymentId: string, documentId: string, companyId?: string) {
  return `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/documents/${encodeURIComponent(documentId)}?${params({ companyId })}`;
}

export async function listPaymentPromiseLinks(paymentId: string, companyId?: string) {
  return paymentRequest<{ promiseLinks: PaymentPromiseLink[] }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/promises?${params({ companyId })}`);
}

export async function linkPaymentPromise(
  paymentId: string,
  input: { promiseId: string; promiseReference?: string; promisedAmountCents: number; promiseDueDate?: string; confidence: string; matchingFactors?: string[] },
  companyId?: string,
) {
  return paymentRequest<{ promiseLinks: PaymentPromiseLink[] }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/promises`, {
    method: "POST",
    body: JSON.stringify({ companyId, ...input, idempotencyKey: `payment-promise:${paymentId}:${input.promiseId}` }),
  });
}

export async function confirmPaymentPromiseOutcome(paymentId: string, linkId: string, outcome: string, companyId?: string) {
  return paymentRequest<{ promiseLinks: PaymentPromiseLink[] }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/promises/${encodeURIComponent(linkId)}`, {
    method: "POST",
    body: JSON.stringify({ companyId, outcome }),
  });
}

export async function listPaymentClarifications(paymentId: string, companyId?: string) {
  return paymentRequest<{ clarifications: PaymentClarification[] }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/clarifications?${params({ companyId })}`);
}

export async function createPaymentClarification(paymentId: string, clarificationType: string, requestText: string, dueDate?: string, companyId?: string) {
  return paymentRequest<{ clarifications: PaymentClarification[] }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/clarifications`, {
    method: "POST",
    body: JSON.stringify({ companyId, clarificationType, requestText, dueDate, idempotencyKey: `payment-clarification:${paymentId}:${crypto.randomUUID()}` }),
  });
}

export async function getWorkbenchPaymentSummary(accountId: string, companyId?: string) {
  return paymentRequest<{ summary: {
    totalReportedPayments: number;
    totalApprovedPayments: number;
    mostRecentPayment: CanonicalPayment | null;
    currentUnappliedAmountCents: number;
    currentPendingVerificationAmountCents: number;
    openPaymentExceptions: number;
    possibleDuplicates: number;
    relatedPromiseOutcome: PaymentPromiseLink | null;
    recentPayments: CanonicalPayment[];
    recentActivity: Array<{ publicationId: string; target: string; eventType: string; actorName: string; occurredAt: string; metadata: Record<string, unknown> }>;
  } }>(`/api/ar-recovery/payments/workbench-summary?${params({ companyId, accountId })}`);
}

export async function submitClientAllocationInstructions(
  paymentId: string,
  input: {
    accountId: string;
    currency: string;
    note?: string;
    invoices: Array<{
      invoiceId: string;
      invoiceNumber: string;
      invoiceDate?: string;
      dueDate?: string;
      originalAmountCents: number;
      operationalBalanceCents: number;
      allocatedAmountCents: number;
    }>;
  },
  companyId?: string,
) {
  return paymentRequest<{ instructions: Array<{ instructionId: string; invoiceNumber: string; reportedAllocationCents: number; reviewStatus: string }> }>(
    `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/client-allocations`,
    { method: "POST", body: JSON.stringify({ companyId, ...input }) },
  );
}

export async function respondPaymentClarification(paymentId: string, clarificationId: string, text: string, companyId?: string) {
  return paymentRequest<{ clarifications: PaymentClarification[] }>(
    `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/clarifications/${encodeURIComponent(clarificationId)}`,
    { method: "POST", body: JSON.stringify({ companyId, action: "RESPOND", text }) },
  );
}

export async function confirmPaymentMatch(
  paymentId: string,
  match: {
    customerId?: string;
    customerName?: string;
    customerNumber?: string;
    accountId?: string;
    accountNumber?: string;
    placementId?: string;
    reason: string;
  },
  companyId?: string,
) {
  return paymentRequest<{ payment: CanonicalPayment }>(`/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/match`, {
    method: "POST",
    body: JSON.stringify({ companyId, ...match, idempotencyKey: `payment-match:${paymentId}:${crypto.randomUUID()}` }),
  });
}

export async function createPaymentAllocation(
  paymentId: string,
  allocation: {
    accountId: string;
    placementId?: string;
    invoiceId?: string;
    invoiceNumber?: string;
    amountCents: number;
    currency: string;
    allocationType: string;
    overrideReason?: string;
  },
  companyId?: string,
) {
  return paymentRequest<{ allocation: PaymentAllocation; created: boolean; replay: boolean }>(
    `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/allocations`,
    {
      method: "POST",
      body: JSON.stringify({ companyId, ...allocation, idempotencyKey: `allocation:${paymentId}:${crypto.randomUUID()}` }),
    },
  );
}

export async function reversePaymentAllocation(paymentId: string, allocationId: string, reason: string, companyId?: string) {
  return paymentRequest<{ allocation: PaymentAllocation }>(
    `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/allocations/${encodeURIComponent(allocationId)}/reverse`,
    {
      method: "POST",
      body: JSON.stringify({ companyId, reason, idempotencyKey: `allocation-reverse:${allocationId}:${crypto.randomUUID()}` }),
    },
  );
}

export async function listPaymentExceptions(companyId?: string) {
  return paymentRequest<{ exceptions: PaymentException[] }>(`/api/ar-recovery/payments/exceptions?${params({ companyId })}`);
}

export async function createPaymentException(
  input: { paymentId?: string; uploadId?: string; uploadRowId?: string; category: string; reason: string; ownerName?: string; dueDate?: string },
  companyId?: string,
) {
  return paymentRequest<{ exception: PaymentException }>("/api/ar-recovery/payments/exceptions", {
    method: "POST",
    body: JSON.stringify({ companyId, ...input }),
  });
}

export async function updatePaymentException(
  exceptionId: string,
  input: { status: string; ownerName?: string; notes?: string; requestedInformation?: string; dueDate?: string; resolution?: string },
  companyId?: string,
) {
  return paymentRequest<{ exception: PaymentException }>(`/api/ar-recovery/payments/exceptions/${encodeURIComponent(exceptionId)}`, {
    method: "PATCH",
    body: JSON.stringify({ companyId, ...input }),
  });
}

export async function listPaymentUploads(companyId?: string) {
  return paymentRequest<{ uploads: PaymentUpload[] }>(`/api/ar-recovery/payments/uploads?${params({ companyId })}`);
}

export async function uploadPaymentFile(file: File, clientId: string, companyId?: string) {
  const form = new FormData();
  form.set("file", file);
  form.set("clientId", clientId);
  form.set("idempotencyKey", `payment-upload:${file.name}:${file.size}:${file.lastModified}`);
  return paymentRequest<{ upload: PaymentUpload; created: boolean; replay: boolean }>(
    `/api/ar-recovery/payments/uploads?${params({ companyId })}`,
    { method: "POST", body: form },
  );
}

export async function getPaymentUpload(uploadId: string, companyId?: string) {
  return paymentRequest<{ upload: PaymentUpload; rows: PaymentUploadRow[] }>(
    `/api/ar-recovery/payments/uploads/${encodeURIComponent(uploadId)}?${params({ companyId })}`,
  );
}

export async function decidePaymentUploadRow(
  uploadId: string,
  rowId: string,
  input: { decision: "accept" | "correct" | "reject" | "exclude" | "reprocess"; normalized?: Record<string, unknown>; reason?: string },
  companyId?: string,
) {
  return paymentRequest<{ row: PaymentUploadRow }>(
    `/api/ar-recovery/payments/uploads/${encodeURIComponent(uploadId)}/rows/${encodeURIComponent(rowId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ companyId, ...input, idempotencyKey: `upload-row:${uploadId}:${rowId}:${input.decision}` }),
    },
  );
}

export async function getPaymentReports(companyId?: string) {
  return paymentRequest<{
    metrics: PaymentMetrics;
    bySource: Array<{ label: string; count: number; amountCents: number }>;
    byMethod: Array<{ label: string; count: number; amountCents: number }>;
    byClient: Array<{ label: string; count: number; amountCents: number }>;
    exceptions: Array<{ label: string; count: number; amountCents: number }>;
  }>(`/api/ar-recovery/payments/reports?${params({ companyId })}`);
}

export async function listAccountPayments(accountId: string, companyId?: string) {
  return paymentRequest<{ payments: CanonicalPayment[] }>(
    `/api/ar-recovery/payments/account-history?${params({ companyId, accountId })}`,
  );
}

export async function uploadPaymentDocument(paymentId: string, file: File, documentType: string, companyId?: string) {
  const form = new FormData();
  form.set("file", file);
  form.set("documentType", documentType);
  return paymentRequest<{ document: PaymentDetail["documents"][number] }>(
    `/api/ar-recovery/payments/${encodeURIComponent(paymentId)}/documents?${params({ companyId })}`,
    { method: "POST", body: form },
  );
}

async function paymentRequest<T>(url: string, init: RequestInit = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: init.body instanceof FormData
      ? init.headers
      : { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  if (!response.ok) throw new Error((await response.text()) || "Payment Center request failed.");
  return response.json() as Promise<T>;
}

function params(input: Record<string, string | number | null | undefined>) {
  const result = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => {
    if (value != null && value !== "") result.set(key, String(value));
  });
  return result.toString();
}
